import {
  AvsError,
  Public,
  Subject
} from '../types';
import { AvsUnresponsive } from '../utilities/health';
import {
  AvsErrorResponse,
  ErrorMessage,
  ErrorMessageType
} from './types';

type ErrorMessageLike = Error | ErrorMessage | Response | string;

/** Public interface of @see ErrorMessageService. */
export type IErrorMessageService = Public<ErrorMessageService>;

/** Service to track errors to display in @see ErrorMessageModal. */
export class ErrorMessageService {
  readonly topErrorChange = new Subject<ErrorMessage | null>();
  private _errors: ErrorMessage[] = [];

  /** The last error that has not been dismissed yet. */
  get topError(): ErrorMessage | null {
    return (this._errors.length) ? this._errors[0] : null;
  }

  /** Add an error for AVS being unresponsive. */
  async addAvsUnresponsiveError(error: AvsError): Promise<void> {
    await this.addError({
      message: (
        <AvsUnresponsive
          error={error}
          onlyShowContent
        />
      ),
      title: window.l.popup.avs_error,
      type: ErrorMessageType.warning
    });
  }

  /** Add a generic error message. */
  async addError(error: ErrorMessageLike): Promise<void> {
    error = await this.createErrorMessage(error);
    this._setErrors([error, ...this._errors]);
  }

  async addWarning(warning: ErrorMessageLike): Promise<void> {
    warning = {
      ...await this.createErrorMessage(warning),
      type: ErrorMessageType.warning
    };
    this._setErrors([warning, ...this._errors]);
  }

  async createErrorMessage(error: ErrorMessageLike): Promise<ErrorMessage> {
    if (typeof error === 'string') {
      error = new Error(error);
    }

    if (error instanceof Error) {
      return {
        message: `${error}`,
        title: error.name,
        type: ErrorMessageType.error
      };
    } else if (!(error instanceof Response)) {
      return error;
    }

    let message: ErrorMessage['message'] = '';
    let json: unknown = null;
    let title = `${error.status} ${error.statusText} Error`;
    try {
      message = await error.text();
      json = JSON.parse((message) ? message as string : 'null');
    } catch {
      // Intentionally left blank.
    }

    if (AvsErrorResponse.is(json)) {
      message = AvsErrorResponse.createError(null, json).message;
      title = 'AVS Error';
    } else {
      const jsonMessage = (
        json
        && typeof json === 'object'
        && 'message' in json
        && typeof json.message === 'string'
      ) ? json.message : null;
      message = (
        <>
          <p>Failed to fetch <code>{error.url}</code></p>
          {(!jsonMessage) ? null : (
            <p>{jsonMessage}</p>
          )}
          <pre>
            {(!json) ? (
              message
            ): (
              JSON.stringify(json, null, 2)
            )}
          </pre>
        </>
      );
    }

    return {
      message,
      title,
      type: ErrorMessageType.error
    };
  }

  /** Remove the top error that is currently being displayed. */
  popError(): void {
    this._setErrors(this._errors.slice(1));
  }

  /** Update errors and push a @see topErrorChange event if needed. */
  private _setErrors(errors: ErrorMessage[]): void {
    const oldTopError = this.topError;
    this._errors = errors;
    if (oldTopError !== this.topError) {
      this.topErrorChange.next(this.topError);
    }
  }
}
